import { useEffect, useState } from "react";
import clsx from "clsx";
import { PortalDialogProps } from "@app/services/modal.service";
import OrderVpsService from "@app/services/http/order.vps.service";
import useObservable from "@core/hooks/use-observable.hook";
import { OrderVpsHistory } from "@app/types";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES } from "@app/constants";
import { formatDate, formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import Modal from "../modal/modal.component";
import Pagination from "../pagination";
import { IcX } from "../icons";
import { iconBtn } from "../ui";

/** Lịch sử thuê (gia hạn) VPS: GET /orders-vps/:id/history */
function OrderVpsHistoryModal({ portalDialogRef, portalData }: PortalDialogProps<{ order_id: string; title?: string }>) {
  const t = useT();
  const order_id = portalData?.order_id || "";
  const [history, setHistory] = useState<OrderVpsHistory[]>([]);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);

  const { subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    order_id &&
      subscribeUntilDestroy(OrderVpsService.getOrderVpsHistory(order_id, page), (res) => {
        setHistory(res?.data || []);
        setTotalPages(res?.total_page || 0);
      });
  }, [order_id, page]);

  const total = history.reduce((sum, h) => sum + (h.price || 0), 0);
  const th = "whitespace-nowrap px-4 py-2.5 font-semibold";

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren={false}>
      <div className="flex max-h-[min(86vh,720px)] w-[min(640px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="text-[19px] font-bold">{t("Lịch sử thuê (gia hạn) VPS")}</h2>
            <p className="m-0 mt-0.5 truncate font-mono text-[12.5px] text-muted">{portalData?.title || order_id}</p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={() => portalDialogRef.close()}>
            <IcX className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          {!history.length ? (
            <p className="m-0 py-8 text-center text-muted">
              {t("Chưa có lịch sử. Đơn thuê trước khi có tính năng này sẽ không có lịch sử.")}
            </p>
          ) : (
            <>
              <table className="htbl w-full border-collapse text-left text-[13px] tabular-nums">
                <thead>
                  <tr className="bg-surface-2 text-xs text-muted">
                    <th className={th}>{t("Số tháng")}</th>
                    <th className={th}>{t("Số tiền")}</th>
                    <th className={th}>{t("Chiết khấu")}</th>
                    <th className={th}>{t("Thời gian thực hiện")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h._id} className="border-b border-line last:border-b-0">
                      <td data-label={t("Số tháng")} className="px-4 py-2.5 font-semibold">
                        {h.rental_period} {t("tháng")}
                        {h.type === "create" && (
                          <em className="ml-1.5 rounded bg-brand-soft px-1.5 py-px text-[11px] not-italic text-brand">{t("Thuê mới")}</em>
                        )}
                      </td>
                      <td data-label={t("Số tiền")} className="px-4 py-2.5">{formatMoneyVN(h.price)}đ</td>
                      <td data-label={t("Chiết khấu")} className="px-4 py-2.5">{formatMoneyVN(h.discount || 0)}đ</td>
                      <td data-label={t("Thời gian thực hiện")} className="px-4 py-2.5">{formatDate(h.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages <= 1 && (
              <p className="m-0 mt-3 text-right text-[13px] text-muted">
                {t("Tổng")}: <b className="tabular-nums text-fg">{formatMoneyVN(total)}đ</b>
              </p>
              )}
            </>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center border-t border-line px-6 py-3">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default OrderVpsHistoryModal;
